/**
 * THE MANAGER'S DISPATCH — one message per rep, not a report.
 *
 * Brian: "I need to export the different stages of where we are and asking for
 * a review so that I can quickly shoot a message to each team member sorting by
 * representative and make sure they know these people need to be asked for a
 * review."
 *
 * He is not analysing coverage. He is about to send messages. So the unit of
 * output is a BLOCK OF TEXT ADDRESSED TO ONE PERSON that can be pasted into
 * Slack or an email without a single edit — and if he has to sort, filter or
 * assemble anything after the export, this file has failed at its only job.
 *
 * PURE, and deliberately almost zero-import so verify-dispatch.mjs drives this
 * exact code under Node. The one import is `ownerName` — see below, it is
 * load-bearing.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 1. THE STAGES ARE NOT NEW WORDS.
 *
 * `reviewCoverage.js` already models this universe and holds 202 assertions
 * about it. Inventing a second vocabulary here — "pending", "sent", "complete"
 * — would be the `stageTally` failure again in a new place: two files naming
 * the same states differently, drifting, and a rep reading a word the model
 * does not know. So every label below is IMPORTED from the module that owns it
 * rather than retyped:
 *
 *   To ask        COVERAGE_GROUPS[TO_ASK].label      — reviewCoverage.js
 *   Fully asked   COVERAGE_OVERLAYS[allAsked].label  — reviewCoverage.js
 *   Do not ask    COVERAGE_OVERLAYS[suppressed].label— reviewCoverage.js
 *   Landed        the Reviews tab's own word
 *
 * `verify-dispatch.mjs` §1 asserts the identity — not that the strings look
 * alike, but that they ARE the same values — because matching prose is what
 * drifts.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 2. WHO SOMEBODY IS, DECIDED ONCE.
 *
 * `ownerName()` in lib/championships.js is the ONE place in this repo that
 * turns a DTO's owner field into a display name: it coerces a string, a number
 * or an owner object, and treats '' / Unassigned / unknown / none / n/a / '-'
 * as NOBODY. A review order names its people in `reps: []`, which `ownerName`
 * does not read — so this file feeds it each value as `{ rep: value }` rather
 * than reimplementing the coercion and the blank vocabulary.
 *
 * THAT DISTINCTION IS THE WHOLE POINT. A second blank-name rule here would go
 * stale the first time somebody adds a spelling of "nobody" to championships.js
 * — and a rep silently dropped out of a dispatch list is a rep who never gets
 * told. Reusing the rule means this file cannot disagree with the Command
 * Center about whether "n/a" is a person.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 3. THE FLAGGED ONES ARE THE POINT.
 *
 * Brian: "1 one-star review does exponential damage."
 *
 * A do-not-ask account is a decision a PERSON made, with a reason, and it takes
 * the account out of every rep's queue. If it appears anywhere near an ask list
 * — same section, same ordering, same styling — somebody working down the list
 * at 4pm will ask it. So:
 *
 *   · it is a SEPARATE stage, last, never merged into the ask list;
 *   · the reason travels with it, always, because "do not ask" with no reason
 *     is an instruction a rep will second-guess and override;
 *   · `flaggedSafe` is a checkable property, not a promise: it asserts that no
 *     record in the DO-NOT-ASK set also appears in the ask set. If the two ever
 *     intersect the screen says so in alarm rather than printing both lists.
 *
 * ⚠️ AND THE LISTING MAY BE SHORT — WHICH IS REPORTED, NOT PAPERED OVER.
 * `suppressed` is an OVERLAY in the tally: it is counted on top of whichever
 * state the order is in, and the queue route does not promise to return the
 * suppressed ROWS. So the count Brian sees in coverage can exceed the rows this
 * export can list. `dispatchTally` carries `flaggedCounted` vs `flaggedListed`
 * and the panel states the shortfall by name.
 *
 * The safety direction matters and is worth being precise about: a suppressed
 * row ABSENT from the queue is safe (it is in nobody's ask list either). The
 * dangerous case is a suppressed row PRESENT and unflagged, which is why
 * `isFlagged` reads every marker the backend might use rather than one.
 *
 * ─────────────────────────────────────────────────────────────────────────
 * 4. THE ARITHMETIC, AND THE TRAP IT AVOIDS.
 *
 * An order can name TWO reps. Both need to know, so it goes in BOTH blocks —
 * and the per-rep counts then sum to MORE than the universe. That is the
 * 203-record summary bar in reverse: a total that looks like a partition and
 * is not one.
 *
 * So two different numbers are reported and never conflated:
 *   `records`    distinct review records in scope
 *   `placements` rep × record rows across the blocks — always >= records
 * and the reconciled property is that every record is placed AT LEAST ONCE.
 * `shared` names how many are deliberately in more than one block, so the
 * excess is explained on screen rather than read as double counting.
 */

import { ownerName, NO_OWNER } from '../../lib/championships.js'
import { COVERAGE_GROUPS, COVERAGE_OVERLAYS, TO_ASK } from './reviewCoverage.js'

/* ── the stages ───────────────────────────────────────────────────────── */

const groupLabel = (key) => COVERAGE_GROUPS.find((g) => g.key === key)?.label || key
const overlayLabel = (key) => COVERAGE_OVERLAYS.find((o) => o.key === key)?.label || key

export const STAGE_TO_ASK = 'to-ask'
export const STAGE_ASKED = 'asked'
export const STAGE_LANDED = 'landed'
export const STAGE_FLAGGED = 'flagged'

/**
 * In dispatch order — what a rep should do first, first. DO NOT ASK is last and
 * alone, because it is the one section that must never be skim-read as part of
 * the one above it.
 */
export const DISPATCH_STAGES = [
  {
    key: STAGE_TO_ASK,
    // Verbatim from reviewCoverage.js — the module that owns the word.
    label: groupLabel(TO_ASK),
    heading: 'ASK THESE',
    blurb: 'Delivered, inside the window, still has something to ask for. This is the work.',
    act: true,
  },
  {
    key: STAGE_ASKED,
    label: overlayLabel('allAsked'),
    heading: 'ALREADY ASKED — WAITING',
    blurb: 'The ask went out and nothing has come back yet. A polite second ask is legitimate; a fourth is noise.',
    act: false,
  },
  {
    key: STAGE_LANDED,
    label: 'Landed',
    heading: 'LANDED',
    blurb: 'A review came back. Nothing to do — this is here so the block is the whole picture, not a to-do list with the wins cut out.',
    act: false,
  },
  {
    key: STAGE_FLAGGED,
    label: overlayLabel('suppressed'),
    heading: 'DO NOT ASK',
    blurb: 'A person decided these accounts must not be asked, and left a reason. Never ask one of these, whatever the queue says.',
    act: false,
  },
]

export const STAGE_BY_KEY = Object.fromEntries(DISPATCH_STAGES.map((s) => [s.key, s]))

/* ── identity ─────────────────────────────────────────────────────────── */

export { NO_OWNER }

/** The dispatch block that catches everything nobody owns. */
export const UNASSIGNED_KEY = ' unassigned'

/**
 * Fold a display name to a grouping key. Case and punctuation only — never a
 * substring, and never a token-subset match.
 *
 * `NC` is a substring of `NCHSAA`, and that exact class of bug shipped once in
 * this repo's prompt composer WITH A TEST THAT AGREED WITH IT. Two people
 * called "Jon Smith" and "Jon Smithers" must not merge into one dispatch block,
 * because one of them then never gets the message.
 */
export function repKey(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[.,]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Every person this record should be dispatched to, as display names.
 *
 * Order of authority:
 *   1. `reps[]`          — who OWNS the account on the ClickUp order
 *   2. `rep` / `leadRep` — read by ownerName directly, for a record shaped like
 *                          a Command Center one
 *   3. `requestedByName` — who made the ask. Only ever reached for an ASKED or
 *                          LANDED record, where the person to chase genuinely
 *                          is the person who asked.
 *
 * Every candidate goes through `ownerName({ rep: v })`, so the blank vocabulary
 * and the object coercion are the shipped ones and not a copy.
 */
export function repsOf(row) {
  const out = []
  const push = (v) => {
    const name = ownerName({ rep: v })
    if (name && !out.some((x) => repKey(x) === repKey(name))) out.push(name)
  }
  if (Array.isArray(row?.reps)) row.reps.forEach(push)
  if (!out.length) push(row?.rep ?? row?.leadRep)
  if (!out.length) push(row?.requestedByName)
  return out
}

/** Where the rep name came from, so a CSV reader never has to guess. */
export function repSourceOf(row) {
  if (Array.isArray(row?.reps) && row.reps.some((v) => ownerName({ rep: v }))) return 'order'
  if (ownerName({ rep: row?.rep ?? row?.leadRep })) return 'order'
  if (ownerName({ rep: row?.requestedByName })) return 'asked-by'
  return 'none'
}

/* ── flagged ──────────────────────────────────────────────────────────── */

const str = (v) => (typeof v === 'string' ? v.trim() : '')

/**
 * Is this record flagged do-not-ask?
 *
 * Deliberately reads SEVERAL markers. The backend's exact field is not pinned
 * in this repo (sr-portal-backend is a separate repo, hard rule #4), and the
 * asymmetry of the risk decides the design: a flagged row read as askable ends
 * with a rep emailing an account somebody deliberately protected, while an
 * askable row read as flagged costs one ask. So this errs toward flagged.
 */
export function isFlagged(row) {
  return !!(
    row?.suppressed ||
    row?.doNotAsk ||
    row?.suppression ||
    row?.suppressedAt ||
    str(row?.suppressedReason) ||
    str(row?.suppression?.reason)
  )
}

/**
 * The human's reason, verbatim. Never invented, and never blank-filled with
 * something plausible — a reason nobody wrote is a reason nobody can stand
 * behind, and the rep is told exactly that.
 */
export const NO_REASON_GIVEN =
  'No reason was recorded. Do not ask, and find out why before lifting the flag.'

export function flagReason(row) {
  return (
    str(row?.suppressedReason) ||
    str(row?.suppression?.reason) ||
    str(row?.reason) ||
    NO_REASON_GIVEN
  )
}

/* ── normalising a record ─────────────────────────────────────────────── */

const TYPE_LABEL = {
  internal: 'Internal', google: 'Google', facebook: 'Facebook',
  instagram: 'Instagram', tiktok: 'TikTok', x: 'X',
}
export const typeLabel = (t) => TYPE_LABEL[t] || String(t || '')

/**
 * 'YYYY-MM-DD' from a date-ish value, using LOCAL parts.
 *
 * ⚠️ TWO OPPOSITE OFF-BY-ONE-DAY TRAPS, AND BOTH ARE LIVE HERE.
 *
 * 1. A TIMESTAMP read through `toISOString()` converts to UTC first, so west of
 *    Greenwich an evening delivery keys to the FOLLOWING day. That is the bug
 *    fixed in the Command Center's `dayKey()` and again in reviewCoverage.js,
 *    and it is why the local getters below are used instead.
 *
 * 2. A DATE-ONLY STRING is the mirror image, and it is the one that actually
 *    fired in `verify-dispatch.mjs`. `new Date('2026-08-29')` is specified to
 *    parse as UTC midnight, so reading LOCAL parts back out of it returns
 *    2026-08-28 anywhere west of Greenwich — a delivered-on date reported a day
 *    early, on a screen whose entire purpose is telling a rep how fresh the
 *    delivery is.
 *
 * So a bare YYYY-MM-DD is taken as the calendar day it plainly says and never
 * routed through Date parsing at all. Anything else is a real instant and is
 * read in local time.
 */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/

export function dayKey(value) {
  if (value == null || value === '') return null
  if (typeof value === 'string') {
    const m = DATE_ONLY.exec(value.trim())
    if (m) return `${m[1]}-${m[2]}-${m[3]}`
  }
  const d = new Date(value)
  if (!Number.isFinite(d.getTime())) return null
  const p = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
}

/**
 * One dispatch record, whatever feed it came from. Everything downstream — the
 * blocks, the message text and the CSV — reads THIS shape, so a new feed is a
 * new `from*` function and nothing else changes.
 */
function record({ id, stage, account, url, contact, contactEmail, ordered, deliveredAt, daysSince, stateWord, ask, note, reason, reps, repSource, raw }) {
  return {
    id: String(id ?? ''),
    stage,
    account: str(account) || '(account not named on the record)',
    url: /^https?:\/\//i.test(str(url)) ? str(url) : '',
    contact: str(contact),
    contactEmail: str(contactEmail),
    ordered: str(ordered),
    deliveredAt: deliveredAt || null,
    deliveredDay: dayKey(deliveredAt),
    daysSince: typeof daysSince === 'number' && Number.isFinite(daysSince) ? daysSince : null,
    stateWord: str(stateWord),
    ask: Array.isArray(ask) ? ask : [],
    note: str(note),
    reason: str(reason),
    reps: reps || [],
    repSource: repSource || 'none',
    raw: raw || null,
  }
}

const STATE_WORD = { peak: 'At peak', warm: 'Still warm', closing: 'Cooling', missed: 'Past the window' }

/** What they ordered, in the words the order already uses. */
function orderedText(r) {
  const bits = []
  if (r?.rings) bits.push(`${r.rings} ring${r.rings === 1 ? '' : 's'}`)
  if (str(r?.sport)) bits.push(str(r.sport))
  if (str(r?.level)) bits.push(str(r.level))
  return bits.join(' · ')
}

/** A queue row → a dispatch record. Flagged rows are routed out first. */
export function fromQueueRow(r) {
  const rd = r?.readiness || {}
  const reps = repsOf(r)
  const repSource = repSourceOf(r)
  if (isFlagged(r)) {
    return record({
      id: r?.orderId, stage: STAGE_FLAGGED, account: r?.account, url: r?.url,
      contact: r?.contact, contactEmail: r?.contactEmail, ordered: orderedText(r),
      deliveredAt: rd.deliveredAt, daysSince: rd.daysSince,
      reason: flagReason(r), reps, repSource, raw: r,
    })
  }
  const outstanding = Array.isArray(r?.outstandingTypes) ? r.outstandingTypes : []
  return record({
    id: r?.orderId,
    stage: outstanding.length ? STAGE_TO_ASK : STAGE_ASKED,
    account: r?.account, url: r?.url,
    contact: r?.contact, contactEmail: r?.contactEmail,
    ordered: orderedText(r),
    deliveredAt: rd.deliveredAt,
    daysSince: rd.daysSince,
    stateWord: STATE_WORD[rd.state] || '',
    ask: outstanding.map(typeLabel),
    note: rd.askWho ? `Ask ${rd.askWho}.` : '',
    reps, repSource, raw: r,
  })
}

/** An outstanding (asked, waiting) review row → a dispatch record. */
export function fromAskedRow(r) {
  return record({
    id: r?.id, stage: STAGE_ASKED, account: r?.account, url: r?.url,
    ordered: orderedText(r),
    daysSince: typeof r?.daysWaiting === 'number' ? r.daysWaiting : null,
    ask: [typeLabel(r?.type)],
    note: r?.followups > 0 ? `Chased ${r.followups}×.` : '',
    reps: repsOf(r), repSource: repSourceOf(r), raw: r,
  })
}

/** A landed review row → a dispatch record. */
export function fromLandedRow(r) {
  return record({
    id: r?.id, stage: STAGE_LANDED, account: r?.account, url: r?.url,
    ordered: orderedText(r),
    deliveredAt: r?.receivedAt,
    ask: [typeLabel(r?.type)],
    note: r?.verifiedAt ? 'Verified.' : (r?.type === 'internal' ? 'Text held.' : 'Self-reported.'),
    reps: repsOf(r), repSource: repSourceOf(r), raw: r,
  })
}

/** An explicitly-listed suppressed row → a dispatch record. */
export function fromFlaggedRow(r) {
  return record({
    id: r?.orderId ?? r?.id, stage: STAGE_FLAGGED, account: r?.account, url: r?.url,
    contact: r?.contact, contactEmail: r?.contactEmail, ordered: orderedText(r),
    reason: flagReason(r), reps: repsOf(r), repSource: repSourceOf(r), raw: r,
  })
}

/* ── the blocks ───────────────────────────────────────────────────────── */

const keyOf = (rec) => `${rec.stage}:${rec.id || rec.account}`

/**
 * Group dispatch records into one block per rep.
 *
 * `unassigned` is its OWN block and is never dropped. Those are precisely the
 * accounts nobody will action on their own — dropping them from the export is
 * how they stay unactioned forever — so they are addressed to Brian by name.
 */
export function groupByRep(records, { unassignedTo = 'Brian' } = {}) {
  const map = new Map()

  const ensure = (key, rep, unassigned) => {
    if (!map.has(key)) {
      map.set(key, {
        key, rep, unassigned,
        stages: Object.fromEntries(DISPATCH_STAGES.map((s) => [s.key, []])),
      })
    }
    return map.get(key)
  }

  for (const rec of records) {
    const reps = rec.reps.length ? rec.reps : []
    if (!reps.length) {
      ensure(UNASSIGNED_KEY, unassignedTo, true).stages[rec.stage].push(rec)
      continue
    }
    for (const rep of reps) ensure(repKey(rep), rep, false).stages[rec.stage].push(rec)
  }

  const blocks = [...map.values()].map((b) => {
    const counts = Object.fromEntries(DISPATCH_STAGES.map((s) => [s.key, b.stages[s.key].length]))
    const total = Object.values(counts).reduce((n, x) => n + x, 0)
    return { ...b, counts, total, toAsk: counts[STAGE_TO_ASK], flagged: counts[STAGE_FLAGGED] }
  })

  /* Most to do first — this is a worklist, so the person with 40 asks is the
   * person to message first. Unassigned sorts LAST so it never displaces a real
   * rep at the top, but it is always present. */
  blocks.sort((a, b) => {
    if (a.unassigned !== b.unassigned) return a.unassigned ? 1 : -1
    return b.toAsk - a.toAsk || b.total - a.total || (a.rep < b.rep ? -1 : 1)
  })
  return blocks
}

/**
 * THE RECONCILIATION — the property the screen asserts, not a sentence it
 * prints.
 *
 * @param blocks   the grouped output
 * @param records  the records that went in
 * @param flaggedCounted  the coverage tally's `suppressed` overlay, which is
 *                        an INDEPENDENT number and therefore the only one that
 *                        does real work here.
 */
export function dispatchTally(blocks, records, flaggedCounted = null) {
  const all = Array.isArray(records) ? records : []
  const byStage = Object.fromEntries(DISPATCH_STAGES.map((s) => [s.key, 0]))
  const stray = []
  for (const r of all) {
    if (Object.prototype.hasOwnProperty.call(byStage, r.stage)) byStage[r.stage] += 1
    else stray.push(r.stage)
  }

  const seen = new Map() // record key -> how many blocks hold it
  for (const b of blocks) {
    for (const s of DISPATCH_STAGES) {
      for (const r of b.stages[s.key]) seen.set(keyOf(r), (seen.get(keyOf(r)) || 0) + 1)
    }
  }

  const records_ = all.length
  const placed = seen.size
  const placements = [...seen.values()].reduce((n, x) => n + x, 0)
  const shared = [...seen.values()].filter((x) => x > 1).length
  const dropped = records_ - placed

  /* THE SAFETY PROPERTY. A record must never be in both an ask list and the
   * do-not-ask list — that is the single failure this export exists to make
   * impossible, and it is checked rather than asserted in prose. */
  const askIds = new Set()
  const flagIds = new Set()
  for (const b of blocks) {
    for (const r of b.stages[STAGE_TO_ASK]) askIds.add(r.id || r.account)
    for (const r of b.stages[STAGE_FLAGGED]) flagIds.add(r.id || r.account)
  }
  const collisions = [...flagIds].filter((id) => askIds.has(id))

  const flaggedListed = byStage[STAGE_FLAGGED]
  const flaggedShort =
    typeof flaggedCounted === 'number' && Number.isFinite(flaggedCounted)
      ? Math.max(0, flaggedCounted - flaggedListed)
      : null

  return {
    byStage,
    stray,
    records: records_,
    placed,
    placements,
    shared,
    dropped,
    reps: blocks.filter((b) => !b.unassigned).length,
    unassignedBlock: blocks.find((b) => b.unassigned) || null,
    collisions,
    flaggedListed,
    flaggedCounted: typeof flaggedCounted === 'number' ? flaggedCounted : null,
    flaggedShort,
    /* Every record reached at least one block, no unknown stage, and no account
     * is both askable and flagged. Deliberately NOT "the counts sum to the
     * total" — with multi-rep orders they legitimately do not. */
    reconciled: dropped === 0 && stray.length === 0 && collisions.length === 0,
  }
}

/* ── the message ──────────────────────────────────────────────────────── */

const line = (rec, i) => {
  const head = `${i}. ${rec.account}`
  const who = rec.contact
    ? `   Ask: ${rec.contact}${rec.contactEmail ? ` <${rec.contactEmail}>` : ' — NO EMAIL ON THE ORDER'}`
    : '   Ask: no named contact on the order — find one before sending'
  const what = [
    rec.ordered && `   ${rec.ordered}`,
    rec.deliveredDay && `   Delivered ${rec.deliveredDay}${rec.daysSince != null ? ` — ${rec.daysSince}d ago` : ''}${rec.stateWord ? ` · ${rec.stateWord}` : ''}`,
    rec.ask.length && `   Ask for: ${rec.ask.join(', ')}`,
    rec.note && `   ${rec.note}`,
    rec.url && `   ${rec.url}`,
  ].filter(Boolean)
  return [head, who, ...what].join('\n')
}

const quietLine = (rec, i) => {
  const bits = [rec.ordered, rec.ask.join(', '), rec.note].filter(Boolean).join(' · ')
  const age = rec.daysSince != null ? ` — ${rec.daysSince}d` : ''
  return `${i}. ${rec.account}${bits ? ` — ${bits}` : ''}${age}`
}

const flagLine = (rec, i) => `${i}. ${rec.account}\n   REASON: ${rec.reason}`

/**
 * The paste-ready block for one rep.
 *
 * Plain text with no markdown, because it is going into Slack, an SMS or an
 * email body and a stray asterisk in the wrong client reads as a typo.
 *
 * DO NOT ASK is last, under a rule of dashes, in capitals, with its own count
 * and every reason. A rep skim-reading from the top must hit an unmistakable
 * boundary before they reach it.
 */
export function repMessage(block, { links = null, today = new Date() } = {}) {
  const stageCount = DISPATCH_STAGES
    .filter((s) => block.counts[s.key] > 0)
    .map((s) => `${block.counts[s.key]} ${s.label.toLowerCase()}`)
    .join(' · ')

  const out = []
  out.push(block.unassigned
    ? `Review asks — NOBODY IS ASSIGNED (${block.total} account${block.total === 1 ? '' : 's'})`
    : `Review asks — ${block.rep}`)
  out.push(dayKey(today) || '')
  out.push(stageCount || 'nothing in any stage')
  if (block.unassigned) {
    out.push('')
    out.push('These orders name no rep, so nobody will pick them up on their own.')
  }

  for (const s of DISPATCH_STAGES) {
    const rows = block.stages[s.key]
    if (!rows.length) continue
    out.push('')
    if (s.key === STAGE_FLAGGED) {
      out.push('------------------------------------------------------------')
      out.push(`${s.heading} (${rows.length}) — FLAGGED BY A PERSON. DO NOT CONTACT.`)
      out.push(s.blurb)
      out.push('')
      rows.forEach((r, i) => out.push(flagLine(r, i + 1)))
      out.push('------------------------------------------------------------')
      continue
    }
    out.push(`${s.heading} (${rows.length})`)
    if (s.act) {
      rows.forEach((r, i) => out.push(line(r, i + 1)))
    } else {
      rows.forEach((r, i) => out.push(quietLine(r, i + 1)))
    }
  }

  if (links && (links.google || links.facebook)) {
    out.push('')
    out.push('Where to send them:')
    if (links.google) out.push(`  Google — ${links.google}`)
    if (links.facebook) out.push(`  Facebook — ${links.facebook}`)
  }

  out.push('')
  out.push('Timing is the whole rule: ask while they are holding the rings. Payment never gates it.')
  return out.join('\n')
}

/* ── the CSV ──────────────────────────────────────────────────────────── */

/**
 * One row per rep × record, sorted by rep then by stage — the record, so it can
 * be opened in Excel and sorted again.
 *
 * CSV rather than xlsx deliberately: the Command Center's export is CSV and a
 * manager who already knows how that file opens should not get a second format
 * to learn. Consistency beats formatting here.
 */
export const DISPATCH_COLUMNS = [
  { key: 'rep', label: 'Rep', get: (r, b) => (b.unassigned ? NO_OWNER : b.rep) },
  { key: 'rep_source', label: 'Rep from', get: (r) => r.repSource },
  { key: 'stage', label: 'Stage', get: (r) => STAGE_BY_KEY[r.stage]?.label || r.stage },
  { key: 'account', label: 'Account', get: (r) => r.account },
  { key: 'contact', label: 'Contact to ask', get: (r) => r.contact },
  { key: 'contact_email', label: 'Contact email', get: (r) => r.contactEmail },
  { key: 'ordered', label: 'What they ordered', get: (r) => r.ordered },
  { key: 'delivered', label: 'Delivered', get: (r) => r.deliveredDay || '' },
  { key: 'days_since', label: 'Days since delivery', get: (r) => (r.daysSince == null ? '' : String(r.daysSince)) },
  { key: 'timing', label: 'Timing', get: (r) => r.stateWord },
  { key: 'ask_for', label: 'Ask for', get: (r) => r.ask.join(', ') },
  { key: 'note', label: 'Note', get: (r) => r.note },
  { key: 'do_not_ask_reason', label: 'DO NOT ASK — reason', get: (r) => (r.stage === STAGE_FLAGGED ? r.reason : '') },
  { key: 'order_url', label: 'Order', get: (r) => r.url },
  { key: 'record_id', label: 'Record ID', get: (r) => r.id },
]

const STAGE_ORDER = Object.fromEntries(DISPATCH_STAGES.map((s, i) => [s.key, i]))

export function dispatchRows(blocks) {
  const rows = [DISPATCH_COLUMNS.map((c) => c.label)]
  for (const b of blocks) {
    const flat = []
    for (const s of DISPATCH_STAGES) for (const r of b.stages[s.key]) flat.push(r)
    flat.sort((x, y) => STAGE_ORDER[x.stage] - STAGE_ORDER[y.stage] || (x.account < y.account ? -1 : 1))
    for (const r of flat) rows.push(DISPATCH_COLUMNS.map((c) => c.get(r, b)))
  }
  return rows
}

export function dispatchFilename(now = new Date()) {
  return `srchamp-review-dispatch_by-rep_${dayKey(now)}.csv`
}
